import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Play, Pause, Square, RefreshCw, Activity, Database, 
  Clock, AlertCircle, CheckCircle2, Loader2, Settings,
  TrendingUp, BarChart3, Server
} from "lucide-react";
import { toast } from "sonner";
import useSSE from "../hooks/useSSE";

const DashboardPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);

  // SSE for live updates
  const { data: sseData, connected, reconnect } = useSSE(`${API}/pipeline/stream`, token);

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/pipeline/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch configs
  const fetchConfigs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/config/?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setConfigs(data.configs || []);
        if (data.configs?.length > 0 && !selectedConfig) {
          setSelectedConfig(data.configs[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch configs:", error);
    }
  }, [token, selectedConfig]);

  // Fetch logs for active run
  const fetchLogs = useCallback(async (runId) => {
    try {
      const response = await fetch(`${API}/pipeline/runs/${runId}/logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    fetchConfigs();
    const interval = setInterval(fetchStats, 15000); // Poll every 15s instead of 5s
    return () => clearInterval(interval);
  }, [fetchStats, fetchConfigs]);

  // Handle SSE updates
  useEffect(() => {
    if (sseData) {
      if (sseData.type === "run_update" || sseData.type === "progress") {
        fetchStats();
      }
      if (sseData.type === "initial" && sseData.run) {
        fetchLogs(sseData.run.run_id);
      }
      // Handle streamed logs
      if (sseData.type === "log") {
        setLogs(prev => {
          const newLog = {
            timestamp: sseData.timestamp,
            level: sseData.level,
            stage: sseData.stage,
            message: sseData.message
          };
          // Keep max 50 logs, newest first
          return [newLog, ...prev].slice(0, 50);
        });
      }
    }
  }, [sseData, fetchStats, fetchLogs]);

  // Fetch logs when active run changes
  useEffect(() => {
    if (stats?.active_run_id) {
      fetchLogs(stats.active_run_id);
    }
  }, [stats?.active_run_id, fetchLogs]);

  // Pipeline actions
  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      let url = `${API}/pipeline/${action}`;
      let method = "POST";
      let body = null;

      if (action === "start" && selectedConfig) {
        url = `${API}/pipeline/start?config_id=${selectedConfig}`;
      }

      const response = await fetch(url, {
        method,
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body,
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || `${action} successful`);
        fetchStats();
      } else {
        toast.error(data.detail || `${action} failed`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} pipeline`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceReset = async () => {
    if (!window.confirm("Force reset all stale pipelines? This will cancel any stuck runs.")) {
      return;
    }
    setActionLoading("force-reset");
    try {
      const response = await fetch(`${API}/pipeline/force-reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Force reset complete");
        fetchStats();
      } else {
        toast.error(data.detail || "Force reset failed");
      }
    } catch (error) {
      toast.error("Failed to force reset");
    } finally {
      setActionLoading(null);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStatusColor = (status) => {
    const colors = {
      idle: "bg-slate-500/20 text-slate-400",
      running: "bg-blue-500/20 text-blue-400",
      paused: "bg-yellow-500/20 text-yellow-400",
      completed: "bg-green-500/20 text-green-400",
      failed: "bg-red-500/20 text-red-400",
    };
    return colors[status] || colors.idle;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isRunning = stats?.pipeline_status === "running";
  const isPaused = stats?.pipeline_status === "paused";
  const isIdle = !isRunning && !isPaused;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Pipeline Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">
            13F Institutional Holdings Ingestion Control
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`} />
          <span className="text-xs text-muted-foreground">
            {connected ? 'Connected' : 'Reconnecting...'}
          </span>
          {!connected && (
            <button 
              onClick={reconnect}
              className="text-xs text-blue-400 hover:text-blue-300 underline ml-1"
            >
              Retry
            </button>
          )}
        </div>
      </div>

      {/* Pipeline Status & Controls */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <p className="label-xs mb-1">Pipeline Status</p>
                <Badge className={`${getStatusColor(stats?.pipeline_status)} rounded-sm px-3 py-1 text-sm`}>
                  {stats?.pipeline_status?.toUpperCase() || "IDLE"}
                </Badge>
              </div>
              
              {stats?.active_stage && (
                <div>
                  <p className="label-xs mb-1">Current Stage</p>
                  <p className="font-mono text-sm">{stats.active_stage.replace(/_/g, ' ')}</p>
                </div>
              )}
              
              {stats?.elapsed_seconds && (
                <div>
                  <p className="label-xs mb-1">Elapsed</p>
                  <p className="font-mono text-sm">{formatDuration(stats.elapsed_seconds)}</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isIdle && (
                <>
                  <select
                    value={selectedConfig || ""}
                    onChange={(e) => setSelectedConfig(e.target.value)}
                    className="bg-background border border-border rounded-sm px-3 py-2 text-sm"
                    data-testid="config-select"
                  >
                    {configs.map((c) => (
                      <option key={c.id} value={c.id}>
                        {`${c.name} (v${c.version})`}
                      </option>
                    ))}
                  </select>
                  <Button
                    onClick={() => handleAction("start")}
                    disabled={actionLoading === "start" || !selectedConfig}
                    className="rounded-sm"
                    data-testid="start-pipeline-btn"
                  >
                    {actionLoading === "start" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Start
                  </Button>
                </>
              )}
              
              {isRunning && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handleAction("pause")}
                    disabled={actionLoading === "pause"}
                    className="rounded-sm"
                    data-testid="pause-pipeline-btn"
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    Pause
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleAction("stop")}
                    disabled={actionLoading === "stop"}
                    className="rounded-sm"
                    data-testid="stop-pipeline-btn"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Stop
                  </Button>
                </>
              )}
              
              {isPaused && (
                <>
                  <Button
                    onClick={() => handleAction("resume")}
                    disabled={actionLoading === "resume"}
                    className="rounded-sm"
                    data-testid="resume-pipeline-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Resume
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleAction("stop")}
                    disabled={actionLoading === "stop"}
                    className="rounded-sm"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Stop
                  </Button>
                </>
              )}

              {/* Force Reset - always visible when there might be a stuck run */}
              {(isRunning || isPaused || !connected) && (
                <Button
                  variant="outline"
                  onClick={handleForceReset}
                  disabled={actionLoading === "force-reset"}
                  className="rounded-sm border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                  data-testid="force-reset-btn"
                  title="Force cancel any stuck/stale pipelines"
                >
                  {actionLoading === "force-reset" ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Force Reset
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard
          label="Funds Discovered"
          value={stats?.counters?.funds_discovered || 0}
          icon={Database}
        />
        <StatCard
          label="Funds Selected"
          value={stats?.counters?.funds_selected || 0}
          icon={TrendingUp}
        />
        <StatCard
          label="Tasks Completed"
          value={stats?.counters?.tasks_completed || 0}
          total={(stats?.counters?.tasks_pending || 0) + (stats?.counters?.tasks_completed || 0) + (stats?.counters?.tasks_failed || 0) || null}
          icon={CheckCircle2}
          variant="success"
        />
        <StatCard
          label="Tasks Failed"
          value={stats?.counters?.tasks_failed || 0}
          icon={AlertCircle}
          variant="error"
        />
        <StatCard
          label="Holdings Extracted"
          value={stats?.counters?.holdings_extracted || 0}
          icon={BarChart3}
        />
        <StatCard
          label="Holdings Ingested"
          value={stats?.counters?.holdings_ingested || 0}
          icon={Server}
        />
        <StatCard
          label="Unresolved CUSIPs"
          value={stats?.counters?.unresolved_cusips || 0}
          icon={AlertCircle}
          variant={stats?.counters?.unresolved_cusips > 0 ? "warning" : "default"}
        />
        <StatCard
          label="Queue Depth"
          value={stats?.queue_depth || 0}
          icon={Activity}
        />
      </div>

      {/* Progress & Logs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Progress */}
        <Card className="lg:col-span-2 bg-card border-border rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pipeline Progress</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Live Progress Banner (when running) */}
            {(isRunning || isPaused) && stats?.live_progress && (
              <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                    <span className="text-sm font-medium text-blue-400">
                      {stats.live_progress.stage_label || stats.live_progress.stage?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span className="text-sm font-mono text-blue-400">
                    {stats.live_progress.percent || 0}%
                  </span>
                </div>
                <Progress value={stats.live_progress.percent || 0} className="h-2 mb-2" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {stats.live_progress.current || 0} / {stats.live_progress.total || 0}
                  </span>
                  <span className="font-mono">
                    {stats.live_progress.detail || ''}
                  </span>
                </div>
                {stats.live_progress.counters && (
                  <div className="mt-2 flex gap-4 text-xs">
                    {Object.entries(stats.live_progress.counters).map(([key, value]) => (
                      <span key={key} className="text-muted-foreground">
                        {key.replace(/_/g, ' ')}: <span className="text-foreground font-mono">{value}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {stats?.counters && (
              <div className="space-y-4">
                <ProgressRow
                  label="Fund Discovery"
                  value={stats.counters.funds_selected}
                  max={stats.counters.funds_discovered || 1}
                />
                <ProgressRow
                  label="Holdings Extraction"
                  value={stats.counters.tasks_completed}
                  max={(stats.counters.tasks_pending || 0) + (stats.counters.tasks_completed || 0) + (stats.counters.tasks_failed || 0) || 1}
                />
                <ProgressRow
                  label="Data Enrichment"
                  value={stats.counters.holdings_ingested}
                  max={stats.counters.holdings_extracted || 1}
                />
              </div>
            )}
            
            {!isRunning && !isPaused && (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Pipeline is idle. Start a run to see progress.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Live Logs */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Live Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              {logs.length > 0 ? (
                <div className="space-y-1">
                  {logs.slice(0, 20).map((log, i) => (
                    <div
                      key={i}
                      className={`log-entry ${log.level}`}
                    >
                      <span className="opacity-50">{new Date(log.timestamp).toLocaleTimeString()}</span>
                      {" "}{log.message}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No logs yet
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickStat label="API Calls" value={stats?.counters?.cache_misses || 0} />
        <QuickStat label="Cache Hits" value={stats?.counters?.cache_hits || 0} />
        <QuickStat label="API Retries" value={stats?.counters?.api_retries || 0} />
        <QuickStat label="API Errors" value={stats?.counters?.api_errors || 0} />
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, total, icon: Icon, variant = "default" }) => {
  const variants = {
    default: "text-foreground",
    success: "text-green-400",
    error: "text-red-400",
    warning: "text-yellow-400",
  };

  const displayValue = value ?? 0;

  return (
    <Card className="bg-card border-border rounded-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="label-xs">{label}</p>
            <p className={`stat-value ${variants[variant]}`}>
              {displayValue.toLocaleString()}
              {total && <span className="text-sm text-muted-foreground">/{total}</span>}
            </p>
          </div>
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
};

// Progress Row Component
const ProgressRow = ({ label, value, max }) => {
  const safeValue = value ?? 0;
  const safeMax = max ?? 1;
  const percent = safeMax > 0 ? Math.round((safeValue / safeMax) * 100) : 0;
  
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="font-mono text-sm">{safeValue.toLocaleString()} / {safeMax.toLocaleString()}</span>
      </div>
      <Progress value={percent} className="h-2" />
    </div>
  );
};

// Quick Stat Component
const QuickStat = ({ label, value }) => {
  const displayValue = value ?? 0;
  return (
    <div className="bg-card border border-border rounded-sm p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-mono text-lg">{displayValue.toLocaleString()}</p>
    </div>
  );
};

export default DashboardPage;
